export const attempt = <T = unknown>(func: () => T, fallback: T) => {
    try {
        return func();
    } catch {
        return fallback;
    }
};

export const once = (fn: Function | null, context?: any) => {
    let result: any;
    if (typeof fn !== "function") {
        throw new TypeError("Expected a function");
    }

    return function (...args: any) {
        if (fn) {
            // @ts-expect-error
            result = fn.apply(context || this, args);
            fn = null;
        }

        return result;
    };
};

export const throttle = (fn: Function, ms = 300) => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let lastInvocation: any;
    let lastInvocationArgs: any[];

    return function (this: any, ...args: any[]) {
        lastInvocation = this;
        lastInvocationArgs = args;

        if (timeout) return;

        timeout = setTimeout(() => {
            fn.apply(lastInvocation, lastInvocationArgs);
            timeout = undefined;
        }, ms);
    };
};

export const debounce = (fn: Function, ms = 300) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
};

export const debouncePromise = <TIn>(
    fn: (params: TIn) => Promise<TIn>,
    ms = 300
) => {
    let timerId: ReturnType<typeof setTimeout>;

    return function debounced(args: TIn) {
        if (timerId) {
            clearTimeout(timerId);
        }

        return new Promise<TIn>((resolve) => {
            timerId = setTimeout(() => resolve(fn(args)), ms);
        });
    };
};

export const noop = () => {};

export const isFunction = (value: any): value is Function => {
    return typeof value === "function";
};

type TRetryWithDelay<T> = {
    retries?: number;
    delay?: number; //ms
    fn: () => Promise<T>;
};

export const retryWithDelay = async <T = void>({
    retries = 3,
    delay = 300,
    fn
}: TRetryWithDelay<T>) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            return await fn();
        } catch {
            if (attempt < retries) {
                await new Promise((resolve) => setTimeout(resolve, delay));
            } else {
                throw new Error("Retry limit exceeded");
            }
        }
    }
};
